// Editor de formas de pago (multipago) para facturas emitidas.
// Compartido por Facturación y Facturación Especializada. El backend
// (fac_crear_factura) recibe _cab.pagos = [{forma_pago,total,plazo,unidad_tiempo}].
import { Plus, Trash2 } from 'lucide-react';
import { LOCALE } from '@/lib/utils/regional';

export interface PagoCab { forma_pago: string; total: number; plazo: number }

export const FORMAS_PAGO_SRI: [string, string][] = [
  ['01', 'Efectivo / sin sistema financiero'], ['20', 'Transferencia / otros con SF'],
  ['19', 'Tarjeta de crédito'], ['16', 'Tarjeta de débito'], ['17', 'Dinero electrónico'],
  ['18', 'Tarjeta prepago'], ['15', 'Compensación de deudas'], ['21', 'Endoso de títulos'],
];

const usd = (v: number) => Number(v || 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const field = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

export function PagosEditor({ pagos, setPagos, total }: {
  pagos: PagoCab[]; setPagos: (p: PagoCab[]) => void; total: number;
}) {
  const suma = Math.round(pagos.reduce((s, p) => s + (Number(p.total) || 0), 0) * 100) / 100;
  const dif = Math.round((total - suma) * 100) / 100;
  const set = (i: number, patch: Partial<PagoCab>) => setPagos(pagos.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const agregar = () => setPagos([...pagos, { forma_pago: '01', total: Math.max(0, dif), plazo: 0 }]);
  const quitar = (i: number) => setPagos(pagos.length > 1 ? pagos.filter((_, j) => j !== i) : pagos);
  const autocuadrar = () => {
    // pone la diferencia en el último pago (o el total si hay uno solo)
    if (!pagos.length) { setPagos([{ forma_pago: '01', total, plazo: 0 }]); return; }
    const last = pagos.length - 1;
    setPagos(pagos.map((p, j) => (j === last ? { ...p, total: Math.round((Number(p.total) + dif) * 100) / 100 } : p)));
  };

  return (
    <div className="md:col-span-3 space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-muted-foreground">Formas de pago</label>
        <button type="button" onClick={agregar} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
          <Plus className="w-3 h-3" /> Agregar pago
        </button>
      </div>
      <div className="space-y-2">
        {pagos.map((p, i) => (
          <div key={i} className="grid grid-cols-[1fr_8rem_6rem_2rem] gap-2 items-center">
            <select className={field} value={p.forma_pago} onChange={(e) => set(i, { forma_pago: e.target.value })}>
              {FORMAS_PAGO_SRI.map(([c, t]) => <option key={c} value={c}>{t}</option>)}
            </select>
            <input type="number" min={0} step="0.01" className={`${field} text-right`} placeholder="Monto"
              value={p.total} onChange={(e) => set(i, { total: Number(e.target.value) || 0 })} />
            <input type="number" min={0} className={field} placeholder="Plazo días"
              value={p.plazo} onChange={(e) => set(i, { plazo: Number(e.target.value) || 0 })} title="Plazo en días (crédito)" />
            <button type="button" onClick={() => quitar(i)} disabled={pagos.length <= 1}
              className="inline-flex h-9 w-8 items-center justify-center rounded-md text-red-500 hover:bg-red-50 disabled:opacity-30 dark:hover:bg-red-900/20">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className={`text-xs ${dif === 0 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
        Pagos {usd(suma)} de {usd(total)}
        {dif !== 0 && <> · falta {usd(dif)} <button type="button" onClick={autocuadrar} className="ml-1 text-primary hover:underline">cuadrar</button></>}
      </div>
    </div>
  );
}
